class TpsSlideDraw extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('.tps-slide-draw__slides');
    this.cells = this.querySelectorAll('.tps-slide-draw__cell');
    this.prevButton = this.querySelector('.tps-slide-draw__arrow--prev');
    this.nextButton = this.querySelector('.tps-slide-draw__arrow--next');
    this.mobilePrevButton = this.querySelector('.tps-slide-draw__mobile-arrow--prev');
    this.mobileNextButton = this.querySelector('.tps-slide-draw__mobile-arrow--next');
    this.mobilePage = this.querySelector('.tps-slide-draw__mobile-page');
    this.liveRegion = this.querySelector('[data-slide-status]');
    this.flickity = null;

    this.initCompares();

    if (this.dataset.hasSlider === 'true' && this.slider && this.cells.length > 1 && typeof Flickity !== 'undefined') {
      this.initSlider();
    }
  }

  initSlider() {
    this.flickity = new Flickity(this.slider, {
      contain: true,
      prevNextButtons: false,
      pageDots: false,
      adaptiveHeight: true,
      cellAlign: 'left',
      draggable: true,
      accessibility: false
    });

    this.slider.setAttribute('role', 'group');
    this.slider.setAttribute('aria-roledescription', 'carousel');
    this.stripCarouselTabStops();

    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.flickity.previous());
    }

    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.flickity.next());
    }

    if (this.mobilePrevButton) {
      this.mobilePrevButton.addEventListener('click', () => this.flickity.previous());
    }

    if (this.mobileNextButton) {
      this.mobileNextButton.addEventListener('click', () => this.flickity.next());
    }

    this.updateNavState();
    this.updateCarouselA11y(false);
    this.flickity.on('change', () => {
      this.updateNavState();
      this.updateCarouselA11y(true);
      this.stripCarouselTabStops();
    });
    this.flickity.on('ready', () => {
      this.updateNavState();
      this.updateCarouselA11y(false);
      this.stripCarouselTabStops();
    });
  }

  stripCarouselTabStops() {
    [this.slider, ...this.querySelectorAll('.flickity-viewport, .flickity-slider, .tps-slide-draw__cell')].forEach((el) => {
      if (el) el.removeAttribute('tabindex');
    });
  }

  updateNavState() {
    if (!this.flickity) return;

    const isFirst = this.flickity.selectedIndex <= 0;
    const isLast = this.flickity.selectedIndex >= this.cells.length - 1;

    [this.prevButton, this.mobilePrevButton].forEach((button) => {
      if (!button) return;
      button.disabled = isFirst;
    });

    [this.nextButton, this.mobileNextButton].forEach((button) => {
      if (!button) return;
      button.disabled = isLast;
    });

    if (this.mobilePage) {
      this.mobilePage.textContent = (this.flickity.selectedIndex + 1) + '/' + this.cells.length;
    }
  }

  updateCarouselA11y(announce) {
    if (!this.cells.length) return;

    const currentIndex = this.flickity ? this.flickity.selectedIndex : 0;
    const total = this.cells.length;

    this.cells.forEach((cell, index) => {
      const selected = index === currentIndex;
      cell.setAttribute('aria-hidden', selected ? 'false' : 'true');
      cell.setAttribute('aria-label', 'Slide ' + (index + 1) + ' of ' + total);
    });

    if (announce && this.liveRegion) {
      this.liveRegion.textContent = 'Slide ' + (currentIndex + 1) + ' of ' + total;
    }
  }

  initCompares() {
    const compares = this.querySelectorAll('.tps-draw-compare');
    compares.forEach((compare) => this.bindCompare(compare));
  }

  bindCompare(compare) {
    const frame = compare.querySelector('.tps-draw-compare__frame');
    const range = compare.querySelector('.tps-draw-compare__range');
    const divider = compare.querySelector('.tps-draw-compare__divider');
    if (!frame || !range) return;

    range.setAttribute('tabindex', '-1');
    range.setAttribute('aria-hidden', 'true');

    const stopBubble = (event) => {
      event.stopPropagation();
    };
    ['pointerdown', 'touchstart', 'mousedown'].forEach((eventName) => {
      frame.addEventListener(eventName, stopBubble, { passive: true });
    });

    const updateHandleOffset = (safeValue) => {
      const rect = frame.getBoundingClientRect();
      if (!rect.width) {
        compare.style.setProperty('--tps-handle-offset', '0px');
        return;
      }

      const handleHalf = 19.5;
      const currentX = (safeValue / 100) * rect.width;
      const safeX = Math.max(handleHalf, Math.min(rect.width - handleHalf, currentX));
      compare.style.setProperty('--tps-handle-offset', (safeX - currentX) + 'px');
    };

    const setPosition = (value) => {
      const safeValue = Math.max(0, Math.min(100, value));
      compare.style.setProperty('--tps-compare-position', safeValue + '%');
      updateHandleOffset(safeValue);
      range.value = safeValue;
    };

    setPosition(parseFloat(range.value || 50));

    range.addEventListener('input', (event) => {
      setPosition(parseFloat(event.target.value || 50));
    });

    const updateFromPointer = (clientX) => {
      const rect = frame.getBoundingClientRect();
      if (!rect.width) return;
      const position = ((clientX - rect.left) / rect.width) * 100;
      setPosition(position);
    };

    let dragging = false;

    const onPointerMove = (event) => {
      if (!dragging) return;
      updateFromPointer(event.clientX);
    };

    const stopDrag = () => {
      if (!dragging) return;
      dragging = false;
      compare.classList.remove('is-dragging');
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', stopDrag);
      window.removeEventListener('pointercancel', stopDrag);
    };

    const dragTarget = divider || frame;

    dragTarget.addEventListener('pointerdown', (event) => {
      event.stopPropagation();
      dragging = true;
      compare.classList.add('is-dragging');
      if (divider && divider.setPointerCapture) {
        divider.setPointerCapture(event.pointerId);
      }
      updateFromPointer(event.clientX);
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', stopDrag);
      window.addEventListener('pointercancel', stopDrag);
    });

    window.addEventListener('resize', () => {
      setPosition(parseFloat(range.value || 50));
    });
  }
}

if (!customElements.get('tps-slide-draw')) {
  customElements.define('tps-slide-draw', TpsSlideDraw);
}
